import { useRef, useState, type DragEvent } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import styles from './Dropzone.module.css'

interface DropzoneProps {
  file: File | null
  onFile: (file: File) => void
  /** MIME type accepted, e.g. application/pdf. */
  accept?: string
  title: string
  description?: string
  replaceHint?: string
  error?: string
  /** Inline message when the dropped file's type is not accepted. */
  rejectedMessage?: string
}

/** `accept` may be a comma list of MIME types, with `image/*` style wildcards. */
function accepts(accept: string, type: string): boolean {
  return accept
    .split(',')
    .map((a) => a.trim())
    .some((a) => (a.endsWith('/*') ? type.startsWith(a.slice(0, -1)) : a === type))
}

/** Dashed attach area. Click, keyboard or drop; a wrong type is rejected with an inline error. */
export function Dropzone({
  file,
  onFile,
  accept = 'application/pdf',
  title,
  description,
  replaceHint = 'Toque para substituir',
  error,
  rejectedMessage = 'Formato não aceito. Envie um arquivo PDF.',
}: DropzoneProps) {
  const input = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [rejected, setRejected] = useState(false)

  function take(f: File | undefined) {
    if (!f) return
    if (accept && !accepts(accept, f.type)) {
      setRejected(true)
      return
    }
    setRejected(false)
    onFile(f)
  }

  function onDrop(e: DragEvent) {
    e.preventDefault()
    setOver(false)
    take(e.dataTransfer.files[0])
  }

  const message = error ?? (rejected ? rejectedMessage : undefined)

  return (
    <div>
      <button
        type="button"
        className={cx(styles.zone, file && styles.attached, over && styles.over)}
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <Icon name={file ? 'description' : 'upload'} size={22} color="var(--color-primary)" />
        <span className={styles.title}>{file ? file.name : title}</span>
        <span className={styles.description}>{file ? replaceHint : description}</span>
      </button>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        data-testid="dropzone-input"
        onChange={(e) => {
          take(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      {message && <div className={styles.error}>{message}</div>}
    </div>
  )
}
